import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Spinner } from "@/ui/Button";
import { Field, Input } from "@/ui/Field";
import { AuthCard, FormError, FormNotice } from "@/features/auth/AuthCard";
export const Route = createFileRoute("/_public/verify-email")({
  component: VerifyEmailScreen,
  validateSearch: (search: Record<string, unknown>): { token?: string; email?: string } => ({
    ...(typeof search.token === "string" ? { token: search.token } : {}),
    ...(typeof search.email === "string" ? { email: search.email } : {}),
  }),
});

/**
 * Two jobs in one screen: redeem a link that was clicked, or ask for a new one.
 * Which job it does depends on whether a token is present, which is what the
 * person arriving here already knows.
 */
function VerifyEmailScreen() {
  const { token, email: initialEmail } = Route.useSearch();
  const [email, setEmail] = useState(initialEmail ?? "");

  const verify = useMutation({
    mutationFn: (value: string) => api.post<{ message: string }>("/auth/verify-email", { token: value }),
  });

  const resend = useMutation({
    mutationFn: () => api.post<{ message: string }>("/auth/resend-verification", { email }),
  });

  useEffect(() => {
    if (token) verify.mutate(token);
    // Redeeming happens once, on arrival, for the token in the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (token) {
    return (
      <AuthCard
        title="Confirming your email"
        footer={
          <Link to="/sign-in" className="font-medium text-[var(--primary)] hover:underline">
            Back to sign in
          </Link>
        }
      >
        {verify.isPending && (
          <div className="flex items-center gap-3 py-2 text-sm text-[var(--text-secondary)]">
            <span className="relative h-4 w-4">
              <Spinner className="text-[var(--primary)]" />
            </span>
            Checking your link…
          </div>
        )}

        {verify.isSuccess && (
          <div className="flex flex-col gap-4">
            <FormNotice title="Your email address is confirmed.">
              You can sign in now.
            </FormNotice>
            <Link to="/sign-in">
              <Button tone="primary" wide>
                Continue to sign in
              </Button>
            </Link>
          </div>
        )}

        {verify.isError && (
          <div className="flex flex-col gap-4">
            <FormError error={verify.error} />
            <p className="text-[13px] text-[var(--text-secondary)]">
              Confirmation links can be used once and expire after 48 hours. Enter your address
              below and we will send a new one.
            </p>
            <ResendForm
              email={email}
              onEmailChange={setEmail}
              pending={resend.isPending}
              done={resend.isSuccess}
              error={resend.error}
              onSubmit={() => resend.mutate()}
            />
          </div>
        )}
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title="Resend confirmation"
      subtitle="We will send a fresh link to your address."
      footer={
        <Link to="/sign-in" className="font-medium text-[var(--primary)] hover:underline">
          Back to sign in
        </Link>
      }
    >
      <ResendForm
        email={email}
        onEmailChange={setEmail}
        pending={resend.isPending}
        done={resend.isSuccess}
        error={resend.error}
        onSubmit={() => resend.mutate()}
      />
    </AuthCard>
  );
}

function ResendForm({
  email,
  onEmailChange,
  pending,
  done,
  error,
  onSubmit,
}: {
  email: string;
  onEmailChange: (value: string) => void;
  pending: boolean;
  done: boolean;
  error: unknown;
  onSubmit: () => void;
}) {
  if (done) {
    return (
      <FormNotice title="Sent.">
        If that address needs confirming, a new link is on its way.
      </FormNotice>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <Field label="Email address" required>
        {({ id, describedBy }) => (
          <Input
            id={id}
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            aria-describedby={describedBy}
          />
        )}
      </Field>

      {error ? <FormError error={error} /> : null}

      <Button type="submit" tone="primary" wide busy={pending}>
        Send confirmation link
      </Button>
    </form>
  );
}
